import React, { useRef, useState } from 'react';

import cx from 'classnames';
import { isNil } from 'ramda';
import type { GroupBase, InputActionMeta } from 'react-select-5';
import { useOnClickOutside } from 'usehooks-ts';

import { Select as BaseSelect } from './base';
import { useCreateOption } from './hooks/useCreateOption';
import { useDebouncedLoadOptions } from './hooks/useDebouncedLoadOptions';
import { useEditing } from './hooks/useEditing';
import { useMenuLocker } from './hooks/useMenuLocker';
import s from './index.module.css';
import { FeatureRichSelectProps, ReactSelectRef } from './index.types';

/*
  React Select v5 only supports values as Options.
  Lib maintainers suggest using controlled components
  that manage values as Options and convert them to strings
  https://github.com/JedWatson/react-select/issues/3063
  https://github.com/JedWatson/react-select/issues/3761
  https://github.com/JedWatson/react-select/issues/2920
*/

/**
 * A layer on top of the base Select component that adds custom features.
 * Prefer using this component over the base Select.
 */
// Modify this component to add a custom feature (preferrably via a hook).
export function Select<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: FeatureRichSelectProps<Option, IsMulti, Group>) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { lockMenu, debounced, debounceTimeout, editable, preserveInputOnOptionSelect, ...rest } = props;

  const [menuOpen, setMenuOpen] = useState<boolean | undefined>(undefined);
  const [inputValue, setInputValue] = useState('');

  const selectRef: ReactSelectRef<Option, IsMulti, Group> = useRef(null);

  const menuLocker = useMenuLocker<Option, IsMulti, Group>(
    {
      menuOpen: { value: menuOpen, setValue: setMenuOpen },
      selectRef: selectRef,
    },
    {
      lockMenu: props.lockMenu,
      menuIsOpen: props.menuIsOpen,
    },
  );

  const optionCreator = useCreateOption<Option, IsMulti, Group>(
    { selectRef },
    {
      creatable: props.creatable,
      onCreateOption: props.onCreateOption,
      creatingRequiresConfirmation: props.creatingRequiresConfirmation,
      creatingConfirmationMessage: props.creatingConfirmationMessage,
      getNewOptionData: props.getNewOptionData,
      name: props.name,
    },
  );

  const maybeDebouncedLoadOptions = useDebouncedLoadOptions<Option, IsMulti, Group>(
    props.async
      ? {
          async: props.async,
          debounced: props.debounced,
          debounceTimeout: props.debounceTimeout,
          loadOptions: props.loadOptions,
        }
      : { async: props.async },
  );

  const editing = useEditing<Option, IsMulti, Group>(
    {
      inputValue: {
        value: inputValue,
        setValue: setInputValue,
      },
      selectRef,
    },
    {
      editable: editable,
      value: props.value,
      isMulti: props.isMulti,
    },
  );

  const onInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') {
      setInputValue(newValue);
      return newValue;
    }
    if (!preserveInputOnOptionSelect && meta.action === 'set-value') {
      setInputValue('');
    }
    // Do not clear input on blur if menu is force-opened
    if (!menuOpen && !rest.menuIsOpen && !editable && meta.action === 'input-blur') {
      setInputValue('');
      return '';
    }
    return meta.prevInputValue;
  };

  const containerRef = useRef<HTMLDivElement>(null);

  // TODO: this hotfix is needed because the menu is not closing when clicking outside the select after custom delete option
  // TODO: need refactor select
  useOnClickOutside([containerRef], () => {
    if (isNil(menuOpen)) {
      setMenuOpen(false);
      setMenuOpen(undefined);
    }
    menuLocker.onMenuClose();
  });

  return (
    <div ref={containerRef}>
      <BaseSelect<Option, IsMulti, Group>
        defaultOptions
        cacheOptions
        menuIsOpen={menuOpen}
        inputValue={inputValue}
        {...rest} // Customized props beyond this point ↓↓↓ When customizing, make sure to provide an interface for the component users.
        async={rest.async as true}
        creatable={rest.creatable as true}
        onInputChange={(value, meta) => {
          let returnValue = onInputChange(value, meta);
          // @ts-expect-error // https://github.com/JedWatson/react-select/issues/5677
          returnValue ||= editing.onInputChange(value, meta);
          // @ts-expect-error // https://github.com/JedWatson/react-select/issues/5677
          returnValue ||= props.onInputChange?.(value, meta);
          return returnValue;
        }}
        openMenuOnFocus={menuLocker.openMenuOnFocus || props.openMenuOnFocus}
        onMenuOpen={() => {
          props.onMenuOpen?.();
          menuLocker.onMenuOpen();
        }}
        onMenuClose={() => {
          props.onMenuClose?.();
          menuLocker.onMenuClose();
        }}
        onChange={(option, meta) => {
          editing.onChange(option, meta);
          props.onChange?.(option, meta);
        }}
        onBlur={(e) => {
          editing.onBlur();
          props.onBlur?.(e);
        }}
        selectRef={(node) => {
          selectRef.current = node;
          if (props.selectRef) {
            if (typeof props.selectRef === 'function') {
              props.selectRef(node);
              return;
            }
            props.selectRef.current = node;
          }
        }}
        onCreateOption={optionCreator.onCreateOption}
        classNames={{
          ...props.classNames,
          control: (p) => cx(s.control, { [s.focused]: p.isFocused }, props.classNames?.control?.(p)),
          valueContainer: (p) =>
            cx(s.valueContainer, { [s.editable]: editable && !p.isMulti }, props.classNames?.valueContainer?.(p)),
          singleValue: (p) => cx(s.singleValue, props.classNames?.singleValue?.(p)),
          input: (p) => cx(s.input, props.classNames?.input?.(p)),
        }}
        loadOptions={maybeDebouncedLoadOptions}
      />
    </div>
  );
}
